import { PATH_DICTS_SHIFT_REPORT_DEPARTMENTS } from "@/constants";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { FilterType } from "@/lib/filters";
import { ComponentProps, forwardRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import AsyncSelect, { Option, type AsyncSelectBaseProps } from "./AsyncSelect";
import appConfiguration from "./cfg/AppCfg";
import { useCreateDataTableOptions } from "./data-table/hooks/useCreateDataTableOptions";
import { EntityListLink } from "./EntityListLink";
import { ShiftReportDepartmentsService } from "@/modules/shiftReportDepartments/service";
import { ReadShiftReportDepartment } from "@/types/shiftReportDepartment";

interface ShiftReportDepartmentSelectProps extends AsyncSelectBaseProps {
  className?: string;
  defaultItem?: Partial<Option>;
}

export default forwardRef<HTMLButtonElement, ShiftReportDepartmentSelectProps>(
  function ShiftReportDepartmentSelect({ value, defaultItem, ...props }, ref) {
    const { t } = useTranslation();
    const options = useCreateDataTableOptions("shiftReportDepartmentList");
    const queryKey = useDynamicQueryParams<ReadShiftReportDepartment>(
      "shiftReportDepartmentList",
      options,
    );

    const onFilter: ComponentProps<typeof AsyncSelect>["onChange"] =
      useCallback(
        (filterValue) => {
          options.setFilterValue("name", {
            value: filterValue,
            filter: FilterType.IncludesString,
          });
        },
        [options],
      );

    const onChange: ComponentProps<typeof AsyncSelect>["onChange"] =
      useCallback(
        (e, selectedItem) => {
          props.onChange(e, selectedItem);
        },
        [props],
      );

    return (
      <span className="flex items-center gap-1">
        <AsyncSelect
          queryKey={queryKey}
          queryFn={() =>
            ShiftReportDepartmentsService.getList({
              filters: options.filters,
              limit: appConfiguration.selectComponentsPaginationLimit,
            })
          }
          placeholder={t("shiftReportDepartmentSelect.placeholder")}
          loadingLabel={t("shiftReportDepartmentSelect.loading")}
          noResultsLabel={t("shiftReportDepartmentSelect.noResults")}
          searchLabel={t("shiftReportDepartmentSelect.search")}
          enableFilter
          {...props}
          onFilter={onFilter}
          onChange={onChange}
          value={value ? value.toString() : undefined}
          ref={ref}
          defaultItem={defaultItem}
          renderName={(item: ReadShiftReportDepartment) => (
            <div>{item.name}</div>
          )}
        />
        <EntityListLink
          path={PATH_DICTS_SHIFT_REPORT_DEPARTMENTS}
          label={t("shiftReportDepartmentSelect.link")}
        />
      </span>
    );
  },
);
